import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './PromptGenerator.css';
import HelpModal from './HelpModal';
import Toast from './Toast';
import authService from '../services/authService'; // Added import for authService

// Language options organized by tiers (moved outside component to prevent recreation)
const languageOptions = [
    { tier: 'Tier 1 - Optimal Performance', languages: ['English'] },
    { tier: 'Tier 2 - Very Strong', languages: ['Spanish', 'French', 'German', 'Chinese (Mandarin)', 'Japanese', 'Portuguese', 'Italian'] },
    { tier: 'Tier 3 - Strong with Limitations', languages: ['Russian', 'Korean', 'Arabic', 'Hindi', 'Dutch', 'Swedish', 'Norwegian', 'Danish'] }
];

// Language level options with their prompt text (moved outside component to prevent recreation)
const languageLevelOptions = [
    { value: 'Advanced', prompt: 'sophisticated vocabulary and complex ideas' },
    { value: 'Intermediate', prompt: 'everyday language that most people understand' },
    { value: 'Elementary', prompt: 'simple words and short sentences' }
];

const PromptGenerator = ({ isModal = false }) => {
    const location = useLocation();
    
    // State for all form selections - removing selectedAiModel
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedStyle, setSelectedStyle] = useState('');
    const [topicInput, setTopicInput] = useState('');
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [subtopicInput, setSubtopicInput] = useState('');
    const [selectedSubtopic, setSelectedSubtopic] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('English');
    const [selectedLanguageLevel, setSelectedLanguageLevel] = useState('Advanced');
    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [isDisplayNameEdited, setIsDisplayNameEdited] = useState(false);
    const [isManuallyEdited, setIsManuallyEdited] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [areAiButtonsEnabled, setAreAiButtonsEnabled] = useState(false);

    // Toast notification state
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Custom confirmation modal state
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    // Data arrays - removing aiModels
    const [categories, setCategories] = useState([]);
    const [favoriteCategoryId, setFavoriteCategoryId] = useState(null);
    const [styleTemplates, setStyleTemplates] = useState([]);
    const [outcomeTemplates, setOutcomeTemplates] = useState([]);
    const [topicSuggestions, setTopicSuggestions] = useState([]);
    const [subtopicSuggestions, setSubtopicSuggestions] = useState([]);

    // Template data for building prompts
    const [selectedStyleTemplate, setSelectedStyleTemplate] = useState(null);
    const [selectedOutcomeTemplate, setSelectedOutcomeTemplate] = useState(null);

    // Check for saved prompt data on component mount
    useEffect(() => {
        if (location.state?.savedPrompt) {
            // Force immediate state update
            setGeneratedPrompt(location.state.savedPrompt.content);
            setIsManuallyEdited(true);
            setAreAiButtonsEnabled(true);
            // Then call the full population function
            populateFormWithSavedPrompt(location.state.savedPrompt);
        }
    }, [location.state]);

    // Fetch categories on component mount - removing AI models fetch
    useEffect(() => {
        (async () => {
            await fetchCategories();
            // Try server first
            let fav = null;
            try {
                const t = authService.getToken && authService.getToken();
                const r = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
                if (r.ok) {
                    const p = await r.json();
                    fav = (p && (p.FavoriteCategoryId ?? p.favoriteCategoryId)) || null;
                }
            } catch {}
            // Fallback to localStorage
            if (!fav) {
                try { const v = localStorage.getItem('favoriteCategoryId'); if (v) fav = parseInt(v, 10) || null; } catch {}
            }
            if (fav) {
                setFavoriteCategoryId(fav);
                setSelectedCategory(String(fav));
            }
        })();
    }, []);

    // Fetch style and outcome templates when category changes
    useEffect(() => {
        if (selectedCategory) {
            fetchStyleTemplates(selectedCategory);
            fetchOutcomeTemplates(selectedCategory);
        } else {
            setStyleTemplates([]);
            setOutcomeTemplates([]);
        }
    }, [selectedCategory]);

    // Fetch subtopics when topic is selected
    useEffect(() => {
        if (selectedTopic) {
            fetchSubtopics(selectedTopic.id);
        } else {
            setSubtopicSuggestions([]);
        }
    }, [selectedTopic]);

    // Real-time prompt building function - defined before useEffect that uses it
    const buildPromptRealTime = useCallback(() => {
        if (isManuallyEdited) {
            return;
        }
        
        let prompt = '';
        
        // Add style template text if available
        if (selectedStyleTemplate && selectedStyleTemplate.templateText) {
            prompt += selectedStyleTemplate.templateText;
        }
        
        // Add topic and subtopic if available - using the requested format (appears between Style and Outcome)
        if (topicInput.trim()) {
            if (prompt) prompt += '\n\n';
            if (subtopicInput.trim()) {
                prompt += `Focus on ${topicInput.trim()}, specifically addressing ${subtopicInput.trim()}.`;
            } else {
                prompt += `Focus on ${topicInput.trim()}.`;
            }
        }

        // Add outcome template text if available (appears after topic/subtopic)
        if (selectedOutcomeTemplate && selectedOutcomeTemplate.templateText) {
            if (prompt) prompt += '\n\n';
            prompt += selectedOutcomeTemplate.templateText;
        }

        // Add language and level specifications if not defaults
        if (selectedLanguage !== 'English' || selectedLanguageLevel !== 'Advanced') {
            if (prompt) prompt += '\n\n';
            const levelPrompt = languageLevelOptions.find(level => level.value === selectedLanguageLevel)?.prompt || 'sophisticated vocabulary and complex ideas';
            prompt += `Write in ${selectedLanguage}. Use ${levelPrompt}.`;
        }
        
        setGeneratedPrompt(prompt);
        
        // Enable AI buttons if we have a meaningful prompt
        if (prompt.trim()) {
            setAreAiButtonsEnabled(true);
        } else {
            setAreAiButtonsEnabled(false);
        }
    }, [isManuallyEdited, selectedStyleTemplate, selectedOutcomeTemplate, topicInput, subtopicInput, selectedLanguage, selectedLanguageLevel]);

    // Real-time prompt building - only if not manually edited
    useEffect(() => {
        if (!isManuallyEdited) {
            buildPromptRealTime();
        }
    }, [buildPromptRealTime, isManuallyEdited]);

    // Default Display Name: Topic – Subtopic (only when user hasn't manually edited)
    useEffect(() => {
        if (!isDisplayNameEdited) {
            const t = (topicInput || '').trim();
            const s = (subtopicInput || '').trim();
            if (t && s) setDisplayName(`${t} – ${s}`);
            else if (t) setDisplayName(t);
            else setDisplayName('');
        }
    }, [topicInput, subtopicInput, isDisplayNameEdited]);

    // Debounced topic search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (topicInput.length >= 2) {
                fetchTopicSuggestions(topicInput);
            } else {
                setTopicSuggestions([]);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [topicInput]);

    // Debounced subtopic search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (subtopicInput.length >= 2 && selectedTopic) {
                fetchSubtopicSuggestions(subtopicInput, selectedTopic.id);
            } else if (selectedTopic) {
                fetchSubtopics(selectedTopic.id);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [subtopicInput, selectedTopic]);

    // Removing fetchAiModels function

    const fetchCategories = async () => {
        try {
            const response = await fetch('/api/categories/active');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            showToast('Failed to load categories. Please refresh the page.', 'error');
        }
    };

    const fetchStyleTemplates = async (categoryId) => {
        try {
            const response = await fetch(`/api/promptgenerator/style-templates?categoryId=${categoryId}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setStyleTemplates(data);
            return data;
        } catch (error) {
            console.error('Error fetching style templates:', error);
            showToast('Failed to load style templates. Please try again.', 'error');
            throw error;
        }
    };

    const fetchOutcomeTemplates = async (categoryId) => {
        try {
            const response = await fetch(`/api/promptgenerator/outcome-templates?categoryId=${categoryId}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setOutcomeTemplates(data);
            return data;
        } catch (error) {
            console.error('Error fetching outcome templates:', error);
            showToast('Failed to load outcome templates. Please try again.', 'error');
            throw error;
        }
    };

    const fetchTopicSuggestions = async (query) => {
        if (query.length < 2) {
            setTopicSuggestions([]);
            return;
        }
        try {
            const response = await fetch(`/api/topics/search?q=${encodeURIComponent(query)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setTopicSuggestions(data);
        } catch (error) {
            console.error('Error fetching topic suggestions:', error);
            setTopicSuggestions([]);
        }
    };

    const fetchSubtopics = async (topicId) => {
        try {
            const response = await fetch(`/api/promptgenerator/subtopics?topicId=${topicId}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setSubtopicSuggestions(data);
        } catch (error) {
            console.error('Error fetching subtopics:', error);
            setSubtopicSuggestions([]);
        }
    };

    const fetchSubtopicSuggestions = async (query, topicId) => {
        if (query.length < 2) {
            fetchSubtopics(topicId);
            return;
        }
        try {
            const response = await fetch(`/api/promptgenerator/search-subtopics?topicId=${topicId}&q=${encodeURIComponent(query)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setSubtopicSuggestions(data);
        } catch (error) {
            console.error('Error fetching subtopic suggestions:', error);
            setSubtopicSuggestions([]);
        }
    };

    // Removing handleAiModelChange function

    const handleCategoryChange = (e) => {
        const newCategoryId = e.target.value;
        setSelectedCategory(newCategoryId);
        
        // Reset dependent selections
        setSelectedStyle('');
        setSelectedStyleTemplate(null);
        setSelectedOutcomeTemplate(null);
        setGeneratedPrompt('');
        setIsManuallyEdited(false);
        setAreAiButtonsEnabled(false);
        
        // Clear topic and subtopic inputs
        setTopicInput('');
        setSelectedTopic(null);
        setSubtopicInput('');
        setSelectedSubtopic(null);
        setTopicSuggestions([]);
        setSubtopicSuggestions([]);
    };

    const handleStyleChange = (e) => {
        const styleId = e.target.value;
        setSelectedStyle(styleId);
        
        // Find and set the selected style template
        const styleTemplate = styleTemplates.find(t => t.id.toString() === styleId);
        setSelectedStyleTemplate(styleTemplate);

        // Find the matching outcome template by CategoryID and SortOrder
        if (styleTemplate) {
            const matchingOutcomeTemplate = outcomeTemplates.find(
                t => t.categoryId === styleTemplate.categoryId && t.sortOrder === styleTemplate.sortOrder
            );
            setSelectedOutcomeTemplate(matchingOutcomeTemplate);
        } else {
            setSelectedOutcomeTemplate(null);
        }
        
        setIsManuallyEdited(false);
        setAreAiButtonsEnabled(false);
    };

    const handleLanguageChange = (e) => {
        setSelectedLanguage(e.target.value);
        // Don't reset manual edit flag - preserve user's manual edits
    };

    const handleLanguageLevelChange = (e) => {
        setSelectedLanguageLevel(e.target.value);
        // Don't reset manual edit flag - preserve user's manual edits
    };

    const handleTopicInputChange = (e) => {
        const value = e.target.value;
        setTopicInput(value);
        setSelectedTopic(null);
        // Don't reset manual edit flag - preserve user's manual edits
    };

    const handleTopicSelection = (topic) => {
        setSelectedTopic(topic);
        setTopicInput(topic.name);
        setTopicSuggestions([]);
        setSubtopicInput('');
        setSelectedSubtopic(null);
        // Don't reset manual edit flag - preserve user's manual edits
    };

    const handleSubtopicInputChange = (e) => {
        const value = e.target.value;
        setSubtopicInput(value);
        setSelectedSubtopic(null);
        // Don't reset manual edit flag - preserve user's manual edits
    };

    const handleSubtopicSelection = (subtopic) => {
        setSelectedSubtopic(subtopic);
        setSubtopicInput(subtopic.name);
        setSubtopicSuggestions([]);
        // Don't reset manual edit flag - preserve user's manual edits
    };

    const handlePromptManualEdit = (e) => {
        setGeneratedPrompt(e.target.value);
        setIsManuallyEdited(true);
    };

    const handleGetHelp = () => {
        setIsHelpModalOpen(true);
    };

    const handleHelpModalClose = () => {
        setIsHelpModalOpen(false);
    };

    const handleCategorySelectionFromHelp = async (selectedPurpose) => {
        try {
            // Set the style and outcome templates if available
            if (selectedPurpose.suggestedStyleTemplate && selectedPurpose.suggestedOutcomeTemplate) {
                const styleId = selectedPurpose.suggestedStyleTemplate.id.toString();
                
                // Set the templates directly without fetching (let useEffect handle it)
                setSelectedStyleTemplate(selectedPurpose.suggestedStyleTemplate);
                setSelectedOutcomeTemplate(selectedPurpose.suggestedOutcomeTemplate);
                
                // Set the category (this will trigger useEffect to fetch templates)
                const categoryId = selectedPurpose.suggestedCategory.id.toString();
                setSelectedCategory(categoryId);
                
                // Set the style after a short delay to ensure templates are loaded
                setTimeout(() => {
                    setSelectedStyle(styleId);
                }, 200);
            } else {
                // Fallback: reset and fetch templates if not provided
                setSelectedStyle('');
                setSelectedStyleTemplate(null);
                setSelectedOutcomeTemplate(null);
                
                // Set the category (this will trigger useEffect to fetch templates)
                const categoryId = selectedPurpose.suggestedCategory.id.toString();
                setSelectedCategory(categoryId);
            }
            
            setIsManuallyEdited(false);
            setAreAiButtonsEnabled(false);
            setIsHelpModalOpen(false);
            
            showToast('Category selected successfully!', 'success');
        } catch (error) {
            console.error('Error selecting category from help:', error);
            showToast('Failed to select category. Please try again.', 'error');
        }
    };

    // AI Platform button handlers
    const sendToAiPage = () => {
        try {
            const prompt = (generatedPrompt || '').trim();
            // Signal AI page to reset and paste prompt
            sessionStorage.setItem('ai_paste_prompt', prompt);
            sessionStorage.setItem('ai_paste_action', 'replace');
            // Navigate to /ai
            window.location.href = '/ai';
        } catch {}
    };

    const saveGeneratedPrompt = async () => {
        if (!generatedPrompt.trim() || !selectedCategory || !selectedStyle) {
            return;
        }

        try {
            const promptData = {
                categoryId: parseInt(selectedCategory),
                styleTemplateId: parseInt(selectedStyle),
                outcomeTemplateId: selectedOutcomeTemplate ? selectedOutcomeTemplate.id : null,
                aiModelId: 0, // Use default AI model
                userTopic: topicInput.trim(),
                userDetails: subtopicInput.trim(),
                rawTopic: selectedTopic ? selectedTopic.name : topicInput.trim(),
                rawSubtopic: selectedSubtopic ? selectedSubtopic.name : subtopicInput.trim(),
                topicId: selectedTopic ? selectedTopic.id : null,
                subtopicId: selectedSubtopic ? selectedSubtopic.id : null
            };

            const response = await fetch('/api/promptgenerator/generate', {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(promptData)
            });

            if (response.ok) {
                const responseData = await response.json();
            } else {
                const errorText = await response.text();
                console.error('Failed to save generated prompt:', errorText);
            }
        } catch (error) {
            console.error('Error saving generated prompt:', error);
        }
    };

    const savePrompt = async () => {
        if (!generatedPrompt.trim()) {
            showToast('No prompt to save!', 'warning');
            return;
        }

        try {
            if (!authService.isAuthenticated()) {
                showToast('Please log in to save prompts', 'error');
                return;
            }

            // Extract topic and subtopic from prompt content if manually edited
            let extractedTopic = topicInput.trim() || null;
            let extractedSubtopic = subtopicInput.trim() || null;
            
            if (isManuallyEdited) {
                const { rawTopic, rawSubtopic } = extractTopicAndSubtopic(generatedPrompt);
                extractedTopic = rawTopic || extractedTopic;
                extractedSubtopic = rawSubtopic || extractedSubtopic;
            }

            const promptData = {
                title: extractedTopic || 'Untitled Prompt',
                displayName: (displayName || '').trim() || (extractedTopic ? (extractedSubtopic ? `${extractedTopic} – ${extractedSubtopic}` : extractedTopic) : 'Untitled'),
                content: generatedPrompt,
                description: `Generated prompt for ${selectedCategory ? categories.find(c => c.id === parseInt(selectedCategory))?.name : 'Unknown Category'}`,
                // Omit isPublic to allow server to use user default visibility
                categoryId: selectedCategory ? parseInt(selectedCategory) : null,
                topicId: selectedTopic ? selectedTopic.id : null,
                subtopicId: selectedSubtopic ? selectedSubtopic.id : null,
                rawTopic: extractedTopic,
                rawSubtopic: extractedSubtopic,
                styleTemplateId: selectedStyle ? parseInt(selectedStyle) : null,
                outcomeTemplateId: selectedOutcomeTemplate ? selectedOutcomeTemplate.id : null,
                aiModelId: null, // We'll add AI model selection later
                tags: `${selectedCategory ? categories.find(c => c.id === parseInt(selectedCategory))?.name : ''} ${extractedTopic || ''}`.trim()
            };

            const response = await fetch('/api/savedprompts', {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(promptData)
            });

            if (response.ok) {
                showToast('Prompt saved successfully!', 'success');
            } else {
                let serverMsg = null;
                try { serverMsg = await response.json(); } catch { try { serverMsg = await response.text(); } catch {}
                }
                if (serverMsg && typeof serverMsg === 'object' && serverMsg.code === 'free_saved_limit') {
                    showToast(serverMsg.message || 'Free plan limit reached: up to 12 saved prompts.', 'warning');
                } else {
                    console.error('Failed to save prompt:', serverMsg || response.status);
                    showToast('Failed to save prompt. Please try again.', 'error');
                }
            }
        } catch (error) {
            console.error('Error saving prompt:', error);
            showToast('Failed to save prompt. Please try again.', 'error');
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const hideToast = () => {
        setToast(prev => ({ ...prev, show: false }));
    };

    // Utility function to extract topic and subtopic from prompt content
    const extractTopicAndSubtopic = (promptContent) => {
        let rawTopic = null;
        let rawSubtopic = null;

        // Look for the pattern "Focus on {topic}, specifically addressing {subtopic}."
        const focusPattern = /Focus on ([^,]+), specifically addressing ([^.]+)\./;
        const match = promptContent.match(focusPattern);
        
        if (match) {
            rawTopic = match[1].trim();
            rawSubtopic = match[2].trim();
        } else {
            // Look for just "Focus on {topic}."
            const simpleFocusPattern = /Focus on ([^.]+)\./;
            const simpleMatch = promptContent.match(simpleFocusPattern);
            if (simpleMatch) {
                rawTopic = simpleMatch[1].trim();
            }
        }

        return { rawTopic, rawSubtopic };
    };

    // Function to populate form with saved prompt data
    const populateFormWithSavedPrompt = async (savedPrompt) => {
        try {
            // Set the generated prompt content with a slight delay to ensure proper rendering
            setTimeout(() => {
                setGeneratedPrompt(savedPrompt.content);
                setIsManuallyEdited(true);
                setAreAiButtonsEnabled(true);
            }, 100);

            // If we have category data, populate it
            if (savedPrompt.categoryId) {
                setSelectedCategory(savedPrompt.categoryId.toString());
                
                // Fetch templates for this category
                const styleData = await fetchStyleTemplates(savedPrompt.categoryId);
                const outcomeData = await fetchOutcomeTemplates(savedPrompt.categoryId);
                
                // Set style template if available
                if (savedPrompt.styleTemplateId && styleData) {
                    const styleTemplate = styleData.find(st => st.id === savedPrompt.styleTemplateId);
                    if (styleTemplate) {
                        setSelectedStyle(savedPrompt.styleTemplateId.toString());
                        setSelectedStyleTemplate(styleTemplate);
                    }
                }
                
                // Set outcome template if available
                if (savedPrompt.outcomeTemplateId && outcomeData) {
                    const outcomeTemplate = outcomeData.find(ot => ot.id === savedPrompt.outcomeTemplateId);
                    if (outcomeTemplate) {
                        setSelectedOutcomeTemplate(outcomeTemplate);
                    }
                }
            }

            // Set topic if available
            if (savedPrompt.topicId && savedPrompt.topicName) {
                setSelectedTopic({ id: savedPrompt.topicId, name: savedPrompt.topicName });
                setTopicInput(savedPrompt.topicName);
            }

            showToast('Saved prompt loaded successfully!', 'success');
        } catch (error) {
            console.error('Error populating form with saved prompt:', error);
            showToast('Error loading saved prompt data', 'error');
        }
    };

    const copyToClipboard = () => {
        if (generatedPrompt.trim()) {
            navigator.clipboard.writeText(generatedPrompt).then(() => {
                setAreAiButtonsEnabled(true);
                showToast('Copied!', 'success');
                // Save the generated prompt when copied
                saveGeneratedPrompt();
                
                // Auto-hide the toast after 2 seconds
                setTimeout(() => {
                    hideToast();
                }, 2000);
            }).catch((error) => {
                console.error('Failed to copy to clipboard:', error);
                showToast('Failed to copy to clipboard. Please try again.', 'error');
            });
        } else {
            showToast('No prompt to copy!', 'warning');
        }
    };

    const resetForm = () => {
        setShowResetConfirm(true);
    };

    const confirmReset = () => {
        // Reset all form state
        setSelectedCategory('');
        setSelectedStyle('');
        setTopicInput('');
        setSelectedTopic(null);
        setSubtopicInput('');
        setSelectedSubtopic(null);
        setSelectedLanguage('English');
        setSelectedLanguageLevel('Advanced');
        setGeneratedPrompt('');
        setIsManuallyEdited(false);
        setSelectedStyleTemplate(null);
        setSelectedOutcomeTemplate(null);
        setTopicSuggestions([]);
        setSubtopicSuggestions([]);
        setAreAiButtonsEnabled(false);
        
        // Clear style and outcome templates
        setStyleTemplates([]);
        setOutcomeTemplates([]);
        
        setShowResetConfirm(false);
        showToast('Form reset successfully!', 'success');

        // Re-apply favorite category immediately if set
        if (favoriteCategoryId) {
            setSelectedCategory(String(favoriteCategoryId));
        }
    };

    const cancelReset = () => {
        setShowResetConfirm(false);
    };

    // Update step class logic to account for removed AI model step
    const getStepClass = (stepNumber) => {
        switch (stepNumber) {
            case 1:
                return selectedCategory ? 'completed' : 'active';
            case 2:
                return selectedStyle ? 'completed' : selectedCategory ? 'active' : 'inactive';
            case 3:
                return topicInput ? 'completed' : selectedStyle ? 'active' : 'inactive';
            case 4:
                return subtopicInput ? 'completed' : topicInput ? 'active' : 'inactive';
            case 5:
                return selectedLanguage !== 'English' ? 'completed' : selectedStyle ? 'active' : 'inactive';
            case 6:
                return selectedLanguageLevel !== 'Advanced' ? 'completed' : selectedStyle ? 'active' : 'inactive';
            default:
                return 'inactive';
        }
    };

    const getPromptPlaceholder = () => {
        if (!selectedCategory) return 'Select a category to start building your prompt...';
        if (!selectedStyle) return 'Select a style & outcome to continue...';
        return 'Your prompt will appear here as you make selections...';
    };

    const isPromptEditable = () => {
        return selectedStyle && selectedCategory;
    };

    return (
        <div className={`prompt-generator ${isModal ? 'is-modal' : ''}`}>
            {/* Left Panel - Form Controls */}
            <div className="left-panel">
                <h1>Generate Prompt</h1>
                
                {/* Step 1: Category Selection (formerly step 2) */}
                <div className={`form-section ${getStepClass(1)}`}>
                    <h3>
                        <span className="step-number">1</span>
                        Select Category
                        {selectedCategory && (
                            <button
                                style={{ marginLeft: 8 }}
                                className="help-button"
                                onClick={async () => {
                                    try {
                                        const cid = parseInt(selectedCategory);
                                        const t = authService.getToken && authService.getToken();
                                        const isPinned = favoriteCategoryId && String(favoriteCategoryId) === String(selectedCategory);
                                        const newVal = isPinned ? null : cid;
                                        await fetch('/api/userpreferences/favorite-category', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ FavoriteCategoryId: newVal }) });
                                        setFavoriteCategoryId(newVal);
                                        try {
                                            if (newVal) localStorage.setItem('favoriteCategoryId', String(newVal));
                                            else localStorage.removeItem('favoriteCategoryId');
                                        } catch {}
                                        showToast(isPinned ? 'Unpinned favorite category' : 'Pinned favorite category', 'success');
                                    } catch {}
                                }}
                                title="Pin this as favorite"
                            >
                                {favoriteCategoryId && String(favoriteCategoryId) === String(selectedCategory) ? '★ Pinned' : '☆ Pin'}
                            </button>
                        )}
                        <button 
                            className="help-button" 
                            onClick={handleGetHelp}
                            title="Get help choosing the right category"
                        >
                            Get Help!
                        </button>
                    </h3>
                    <select 
                        value={selectedCategory} 
                        onChange={handleCategoryChange}
                        className="form-select"
                    >
                        <option value="">Choose a category...</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Step 2: Style & Outcome Selection (formerly step 3) */}
                {selectedCategory && (
                    <div className={`form-section ${getStepClass(2)}`}>
                        <h3>
                            <span className="step-number">2</span>
                            Select Style & Outcome
                        </h3>
                        <select 
                            value={selectedStyle} 
                            onChange={handleStyleChange}
                            className="form-select"
                        >
                            <option value="">Choose a style & outcome...</option>
                            {styleTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Step 3: Topic Input (formerly step 4) */}
                {selectedStyle && (
                    <div className={`form-section ${getStepClass(3)}`}>
                        <h3>
                            <span className="step-number">3</span>
                            Enter Topic
                        </h3>
                        <div className="autocomplete-container">
                            <input
                                type="text"
                                value={topicInput}
                                onChange={handleTopicInputChange}
                                placeholder="Enter your topic...(Focus on...)"
                                className="form-input"
                            />
                            {topicSuggestions.length > 0 && (
                                <ul className="suggestions-list">
                                    {topicSuggestions.map(topic => (
                                        <li 
                                            key={topic.id} 
                                            onClick={() => handleTopicSelection(topic)}
                                            className="suggestion-item"
                                        >
                                            {topic.name} {topic.isApproved && <span className="approved">✓</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 4: Subtopic Input (formerly step 5) */}
                {topicInput && (
                    <div className={`form-section ${getStepClass(4)}`}>
                        <h3>
                            <span className="step-number">4</span>
                            Enter Details (Optional)
                        </h3>
                        <div className="autocomplete-container">
                            <input
                                type="text"
                                value={subtopicInput}
                                onChange={handleSubtopicInputChange}
                                placeholder="Enter subtopic details... (specifically addressing...)"
                                className="form-input"
                            />
                            {subtopicSuggestions.length > 0 && (
                                <ul className="suggestions-list">
                                    {subtopicSuggestions.map(subtopic => (
                                        <li 
                                            key={subtopic.id} 
                                            onClick={() => handleSubtopicSelection(subtopic)}
                                            className="suggestion-item"
                                        >
                                            {subtopic.name} {subtopic.isApproved && <span className="approved">✓</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* Step 5: Language Selection (formerly step 6) */}
                {selectedStyle && (
                    <div className={`form-section ${getStepClass(5)}`}>
                        <h3>
                            <span className="step-number">5</span>
                            Language (Optional)
                        </h3>
                        <select 
                            value={selectedLanguage} 
                            onChange={handleLanguageChange}
                            className="form-select"
                        >
                            {languageOptions.map((tier, tierIndex) => [
                                <option key={`tier-${tierIndex}`} value="" disabled style={{ fontWeight: 'bold', color: '#495057' }}>
                                    {tier.tier}
                                </option>,
                                ...tier.languages.map((language, langIndex) => (
                                    <option key={`${tierIndex}-${langIndex}`} value={language}>
                                        &nbsp;&nbsp;{language}
                                    </option>
                                ))
                            ])}
                        </select>
                    </div>
                )}

                {/* Step 6: Language Level Selection (formerly step 7) */}
                {selectedStyle && (
                    <div className={`form-section ${getStepClass(6)}`}>
                        <h3>
                            <span className="step-number">6</span>
                            Language Level (Optional)
                        </h3>
                        <select 
                            value={selectedLanguageLevel} 
                            onChange={handleLanguageLevelChange}
                            className="form-select"
                        >
                            {languageLevelOptions.map(level => (
                                <option key={level.value} value={level.value}>
                                    {level.value}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Right Panel - Prompt Display */}
            <div className="right-panel">
                <div className="prompt-header">
                    <label style={{ display:'block', margin:'0 0 6px 6px', color:'#2c3e50', fontWeight:600 }}>Prompt Name</label>
                    <input
                        type="text"
                        value={displayName}
                        onChange={(e) => { setDisplayName(e.target.value); setIsDisplayNameEdited(true); }}
                        placeholder="Topic – Subtopic"
                        style={{ width:'100%', maxWidth: '600px', padding:'10px 12px', border:'2px solid #e9ecef', borderRadius:8, fontSize:'1rem' }}
                    />
                </div>
                
                <div className="prompt-display-area">
                    <div className="prompt-output">
                        <textarea
                            value={generatedPrompt}
                            onChange={handlePromptManualEdit}
                            readOnly={!isPromptEditable()}
                            className={`prompt-textarea ${!generatedPrompt ? 'empty' : ''}`}
                            placeholder={!isPromptEditable() ? getPromptPlaceholder() : "Start editing your prompt..."}
                            style={{ 
                                backgroundColor: !isPromptEditable() ? '#f8f9fa' : 'white',
                                color: !isPromptEditable() ? '#495057' : '#212529'
                            }}
                        />
                    </div>
                    
                    <div className="button-group">
                        <button 
                            onClick={copyToClipboard} 
                            className="copy-button"
                        >
                            Copy to Clipboard
                        </button>
                        <button 
                            onClick={savePrompt} 
                            className="save-button"
                            disabled={!generatedPrompt.trim()}
                        >
                            Save Prompt
                        </button>
                        <button 
                            onClick={resetForm} 
                            className="reset-button"
                        >
                            Clear/Reset
                        </button>
                    </div>
                    
                    <div className="ai-button-group">
                        <button 
                            onClick={sendToAiPage} 
                            className="ai-button"
                            disabled={!areAiButtonsEnabled}
                        >
                            Send to AI Page
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Help Modal */}
            <HelpModal 
                isOpen={isHelpModalOpen}
                onClose={handleHelpModalClose}
                onSelectCategory={handleCategorySelectionFromHelp}
            />

            {/* Toast Notification */}
            <Toast
                message={toast.message}
                type={toast.type}
                show={toast.show}
                onClose={hideToast}
            />

            {/* Custom Reset Confirmation Modal */}
            {showResetConfirm && (
                <div className="reset-modal-overlay">
                    <div className="reset-modal-content">
                        <h2>Confirm Reset</h2>
                        <p>Are you sure you want to clear all form data and reset the prompt generator?</p>
                        <div className="reset-modal-buttons">
                            <button onClick={confirmReset} className="reset-confirm-button">Confirm</button>
                            <button onClick={cancelReset} className="reset-cancel-button">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromptGenerator; 